import React, { useState, useEffect, useContext, useCallback } from "react";
import Request from "common/Request";
import Result from "./Result";
import { parseSystemCPUUsage } from "./api.js";
import ActionsContext from "common/ActionsContext";
import { useBqnId, useClusterStatus } from "common/BqnContext";
import { atBqnTarget } from "common/api";
import View from "common/View";
import useDebouncer from "common/hooks/debouncer";

const LOAD_INTERVAL = 3000;

const Report = () => {
  const actions = useContext(ActionsContext);
  const [request, setRequest] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const bqnId = useBqnId();
  const mode = useClusterStatus()?.mode;

  const debounce = useDebouncer(500 /*ms*/);

  useEffect(() => {
    // Nothing is loaded while the BQN context has not loaded the BQN
    // selected: the command would not carry its option, and the response
    // would be that of the whole cluster. The effect runs again when it does.
    if (bqnId === undefined) {
      return;
    }

    let active = true;

    // Every row of the response reports its BQN, so the usage of the BQN
    // selected needs no attribution of its own.
    const cpuUsageRetrieval = () => {
      return ifCl
        .run(atBqnTarget("show system cpu", mode, bqnId))
        .then((input) => ({ cpus: parseSystemCPUUsage(input) }));
    };

    const doLoad = () => {
      const req = cpuUsageRetrieval().then((data) => {
        if (active) setLiveData(data);
        return data;
      });
      setRequest(req);
    };

    const doBackgroundLoad = () => {
      cpuUsageRetrieval()
        .then((data) => {
          if (active) setLiveData(data);
        })
        .catch((err) => console.error("CPU background fetch failed:", err));
    };

    doLoad();
    const intervalId = setInterval(doBackgroundLoad, LOAD_INTERVAL);
    const unsubscribe = actions.recv("do-load", () => debounce(doLoad));

    return () => {
      active = false;
      clearInterval(intervalId);
      unsubscribe();
      debounce.stop();
    };
  }, [bqnId, mode, debounce]);

  return (
    <Request during={request}>
      {() => (liveData ? <Result {...liveData} /> : null)}
    </Request>
  );
};

const CPUStatus = ({ returnView = null }) => (
  <View title="CPU Usage" returnView={returnView}>
    <Report />
  </View>
);

export default CPUStatus;
